import type { TicketSales as GQLItem } from '../../../generated/api.graphql';

// An event's sold counts, sales and fees per report row. A count of *tickets*, never of recipients — one order
// of four seats is four sold and one buyer — so nothing here may be labelled a recipient count.
export type TicketSalesByType = {
  blueprintId?: number;
  discountCode?: string;
  numberSold: number;
  netSales: number;
  netFees: number;
};

export type BlueprintSales = {
  numberSold: number;
  netSales: number;
  netFees: number;
};

export const fromGql = (item: GQLItem): TicketSalesByType => ({
  blueprintId: item.blueprintId ?? undefined,
  discountCode: item.discountCode ?? undefined,
  numberSold: item.numberSold ?? 0,
  netSales: item.netSales ?? 0,
  netFees: item.netFees ?? 0,
});

const NO_SALES: BlueprintSales = { numberSold: 0, netSales: 0, netFees: 0 };

const add = (sum: BlueprintSales, { numberSold, netSales, netFees }: TicketSalesByType): BlueprintSales => ({
  numberSold: sum.numberSold + numberSold,
  netSales: sum.netSales + netSales,
  netFees: sum.netFees + netFees,
});

/** Totals per blueprint; the report has one row per price and discount, plus blueprint-less list rows. */
export const salesByBlueprint = (rows: TicketSalesByType[]): Map<number, BlueprintSales> => {
  const totals = new Map<number, BlueprintSales>();

  rows.forEach(row => {
    if (row.blueprintId !== undefined) {
      totals.set(row.blueprintId, add(totals.get(row.blueprintId) ?? NO_SALES, row));
    }
  });

  return totals;
};

/** Totals per blueprint and discount code, summed across the prices a code sold at. */
export const salesByDiscount = (rows: TicketSalesByType[]): Map<number, Map<string, BlueprintSales>> => {
  const totals = new Map<number, Map<string, BlueprintSales>>();

  rows.forEach(row => {
    if (row.blueprintId === undefined || row.discountCode === undefined) {
      return;
    }

    const codes = totals.get(row.blueprintId) ?? new Map<string, BlueprintSales>();
    codes.set(row.discountCode, add(codes.get(row.discountCode) ?? NO_SALES, row));
    totals.set(row.blueprintId, codes);
  });

  return totals;
};
